import type { ChartVisualisation } from "../../../components/widgets/types.ts"

const WIDGET_SIZE_PRESETS = ["small", "medium", "large", "full"] as const
type WidgetSizePreset = (typeof WIDGET_SIZE_PRESETS)[number]

const REPORT_FILTER_DIMENSIONS = [
  "project",
  "pm",
  "client",
  "project_type",
  "project_status",
  "status",
  "state"
] as const
type ReportFilterDimension = (typeof REPORT_FILTER_DIMENSIONS)[number]

type ReportWidgetFilters = Partial<Record<ReportFilterDimension, string[]>>

const REPORT_WINDOW_PRESETS = [
  "today",
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "this_month",
  "this_quarter",
  "ytd",
  "last_month",
  "last_quarter",
  "last_year"
] as const
type ReportWindowPreset = (typeof REPORT_WINDOW_PRESETS)[number]

interface ReportWindowPresetValue {
  preset: ReportWindowPreset
}
interface ReportWindowCustomValue {
  from: string
  to: string
}
type ReportWindow = ReportWindowPresetValue | ReportWindowCustomValue

const isCustomWindow = (
  window: ReportWindow | undefined
): window is ReportWindowCustomValue =>
  !!window && "from" in window && "to" in window

// Report-level compare setting: null means "no comparison".
const REPORT_COMPARE_PRESETS = [
  "previous_period",
  "same_period_last_year"
] as const
type ReportComparePreset = (typeof REPORT_COMPARE_PRESETS)[number]
type ReportCompareValue = ReportComparePreset | null

// Per-widget override: "inherit" defers to the report-level setting.
const REPORT_COMPARE_OVERRIDES = [
  "inherit",
  "none",
  "previous_period",
  "same_period_last_year"
] as const
type ReportCompareOverride = (typeof REPORT_COMPARE_OVERRIDES)[number]

interface ReportLayoutItem {
  i: string
  x: number
  y: number
  w: number
  h: number
}

interface ReportWidgetEntry {
  id: string
  spec: string
  title?: string
  size: WidgetSizePreset
  collapsed: boolean
  expandedH?: number
  filters?: ReportWidgetFilters
  compare?: ReportCompareOverride
  visualisation?: ChartVisualisation
}

interface ReportDefinitionV1 {
  version: 1
  widgets: ReportWidgetEntry[]
  layout: ReportLayoutItem[]
  filters: ReportWidgetFilters
  window?: ReportWindow
  compare?: ReportCompareValue
  icon?: string
}

export {
  WIDGET_SIZE_PRESETS,
  REPORT_FILTER_DIMENSIONS,
  REPORT_WINDOW_PRESETS,
  REPORT_COMPARE_PRESETS,
  REPORT_COMPARE_OVERRIDES,
  isCustomWindow,
  type WidgetSizePreset,
  type ReportFilterDimension,
  type ReportWidgetFilters,
  type ReportWindowPreset,
  type ReportWindow,
  type ReportComparePreset,
  type ReportCompareValue,
  type ReportCompareOverride,
  type ReportLayoutItem,
  type ReportWidgetEntry,
  type ReportDefinitionV1
}
